import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios";

import { LoaderService } from "./loader.service";
import { AuthService } from "./auth.service";
import PubSub from "pubsub-js";
import { eventNames } from "../constants/eventNames";
import { jwtDecode } from "jwt-decode";
import { dispatch as extensionDispatch } from "../utils/Extension";
import { toast } from "react-toastify";
import logger from "../utils/logger";
import { detectInjections, sanitizeInput, InjectionDetectionResult } from "../utils/injection-detection";
import AutoLogout from "./autologout.service";

let refreshTokenTimeout: NodeJS.Timeout | undefined;
/**
 * Timestamp (ms) of the last successful refresh response. Used by
 * startRefreshTokenTimer to suppress an immediate re-refresh when invoked
 * via the setAuthToken PubSub subscriber right after a refresh succeeds —
 * a transient cross-document race can make localStorage briefly look like
 * the token is near-expiry, which would otherwise kick off another POST
 * /apis/refresh, which races again, and the loop self-amplifies (see
 * AppRouters.tsx:1195 subscriber → startRefreshTokenTimer:502).
 */
let lastSuccessfulRefreshAt = 0;
// 📊 Throttle for the "succeeded-but-still-expired" paradox diagnostic in
// startRefreshTokenTimer. Module-scope so it persists across calls.
let lastLoopParadoxLogAt = 0;

/**
 * Options for API requests that carry data from known internal sources.
 *
 * - `source: 'extension'` — Data captured by our browser extension (e.g., page elements,
 *   XPath selectors, CSS selectors). These contain legitimate DOM paths that look like
 *   injection payloads to the scanner. The client-side injection scan is skipped;
 *   the backend still performs its own validation.
 * - `source: 'user'` (default) — User-entered data. Full client-side injection scanning applies.
 */
export interface RequestOptions {
  source?: 'extension' | 'user';
  /** Mark this request as a background poll — it will NOT reset the auto-logout timer.
   *  Use this for setInterval-based auto-refresh calls (daemon list, cluster stats, etc.)
   *  that fire regardless of user interaction. */
  backgroundPoll?: boolean;
}

// ============================================================================
// NETWORK RESILIENCE CONFIGURATION
// ============================================================================
const NETWORK_CONFIG = {
  // Request timeout in milliseconds (30 seconds)
  REQUEST_TIMEOUT: 30000,

  // Retry configuration
  MAX_RETRIES: 2,
  RETRY_DELAY_BASE: 1000, // 1 second base delay
  RETRY_DELAY_MAX: 5000,  // 5 seconds max delay

  // Health check configuration
  HEALTH_CHECK_INTERVAL: 30000, // Check every 30 seconds when issues detected
  HEALTH_CHECK_ENDPOINT: '/health', // Backend health endpoint

  // Errors that should trigger retry
  RETRYABLE_ERRORS: ['Network Error', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET']
};

// Connection health state
let connectionHealthy = true;
let healthCheckInterval: NodeJS.Timeout | null = null;
let lastHealthCheckTime = 0;
let consecutiveFailures = 0;

/**
 * Map API URL to a human-readable section name for error logging.
 * Helps quickly identify which module is failing in console output.
 */
function getApiSection(url: string): string {
  if (!url) return 'UNKNOWN';
  const u = url.toLowerCase();
  // TCM module
  if (u.includes('/tcm/test-cases'))    return 'TCM_TEST_CASES';
  if (u.includes('/tcm/test-suites'))   return 'TCM_TEST_SUITES';
  if (u.includes('/tcm/test-plans'))    return 'TCM_TEST_PLANS';
  if (u.includes('/tcm/test-runs'))     return 'TCM_TEST_RUNS';
  if (u.includes('/tcm/defects'))       return 'TCM_DEFECTS';
  if (u.includes('/tcm/requirements'))  return 'TCM_REQUIREMENTS';
  if (u.includes('/tcm/executions'))    return 'TCM_EXECUTIONS';
  if (u.includes('/tcm/'))              return 'TCM';
  // Web Test Lab
  if (u.includes('/projects'))          return 'PROJECTS';
  if (u.includes('/flows') && u.includes('datasource')) return 'FLOW_DATAMATRIX';
  if (u.includes('/flow-report'))       return 'FLOW_REPORT';
  if (u.includes('/flow-group'))        return 'FLOW_GROUPS';
  if (u.includes('/flows'))             return 'FLOWS';
  if (u.includes('/components'))        return 'COMPONENTS';
  if (u.includes('/datasources'))       return 'DATASOURCES';
  if (u.includes('/api-entities') || u.includes('/apis')) return 'APIS';
  // Auth & tokens
  if (u.includes('/auth/'))             return 'AUTH';
  // AI
  if (u.includes('/ai/'))              return 'AI';
  // Metrics
  if (u.includes('/metrics'))           return 'METRICS';
  // Daemon / execution
  if (u.includes('/daemons') || u.includes('/daemon-commands') || u.includes('/cluster')) return 'DAEMON';
  if (u.includes('/executions'))        return 'EXECUTION';
  // License
  if (u.includes('/license'))           return 'LICENSE';
  // Boards / Kanban
  if (u.includes('/boards'))            return 'BOARDS';
  // Misc
  if (u.includes('/groups'))            return 'GROUPS';
  if (u.includes('/connections'))       return 'CONNECTIONS';
  if (u.includes('/scripts'))           return 'SCRIPTS';
  if (u.includes('/variables'))         return 'VARIABLES';
  return 'API';
}

// Background polling endpoints that should NOT reset auto-logout timer.
// These fire on intervals regardless of user activity, so counting them
// as "activity" would keep the session alive indefinitely.
//
// NOTE: Only truly-always-background endpoints belong here. Endpoints like
// /api/daemons, /api/cluster/, and /api/daemon-commands/ are used for BOTH
// background polling AND user-initiated actions (execute, test connection,
// cache sync). Those are handled via the per-request `_backgroundPoll` flag
// set by callers — see RequestOptions.backgroundPoll.
const BACKGROUND_POLL_PATTERNS = [
  '/api/ai/status',
  '/license/checkbyref/',
  '/apis/refresh',
  '/api/auth/refresh',
];

function isBackgroundPollRequest(config?: AxiosRequestConfig & { _backgroundPoll?: boolean }, url?: string): boolean {
  // Check per-request flag first (set by callers via RequestOptions.backgroundPoll)
  if (config && (config as any)._backgroundPoll === true) return true;
  // Fall back to URL pattern matching for always-background endpoints
  if (!url) return false;
  return BACKGROUND_POLL_PATTERNS.some(pattern => url.includes(pattern));
}

type RefreshTokenResponse = {
  access_token: string;
  refresh_token: string;
};

type ApiResponseData<T = any> = {
  success?: boolean;
  payload?: T;
  // Top-level message string — populated by the backend's
  // ApiResponse.unsuccess(String errMsg) factory (see Java
  // controllers/response/ApiResponse.java field `message`). Distinct
  // from `error.message` which only exists when the server built an
  // explicit ApiError; the top-level `message` is the common path
  // for controller-side .unsuccess() calls.
  message?: string;
  error?: {
    code: number;
    description?: string;
    message?: string;
  };
};

type JWTPayload = {
  exp: number;
  [key: string]: any;
};

type TokenRefreshCallback = (token: string) => void;

/**
 * ApiService - Singleton service for all HTTP requests
 *
 * Features:
 * - Axios instance with interceptors
 * - Automatic JWT token refresh
 * - Activity-based session management (keeps session alive while user is active)
 * - Comprehensive logging
 *
 * Usage:
 *   const api = ApiService.getInstance();
 *   api.get('/endpoint');
 *   api.setupActivityListeners(); // Call after login to enable activity tracking
 *   api.cleanupActivityListeners(); // Call on logout to cleanup
 */
export default class ApiService {
  private instance: AxiosInstance;
  private baseUrl: string;
  private authApiUrl: string;
  private withCredentials: boolean;
  private static myInstance: ApiService | null = null;

  // Activity-based token refresh properties
  private activityEvents: string[];
  private boundHandleActivity: () => void;
  private boundHandleVisibilityChange: () => void;
  private lastActivityReset: number;
  private activityResetThrottle: number;
  private isActivityTrackingEnabled: boolean;
  private iframeFocusIntervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * @param baseUrl - Base URL for API requests
   * @param withCredentials - Whether to include auth headers (default: true)
   * @param skipAuthInterceptor - When true, 401 errors are rejected locally instead
   *   of triggering token refresh / globalLogout(). Use this for services that
   *   talk to non-auth servers (agent-server, license-server) whose 401s are
   *   unrelated to the user's auth session.
   */
  constructor(baseUrl?: string, withCredentials: boolean = true, skipAuthInterceptor: boolean = false) {
    // Create Axios instance with timeout configuration
    this.instance = axios.create({
      timeout: NETWORK_CONFIG.REQUEST_TIMEOUT,
    });
    // In development, use proxy path. Proxy has bypass logic to differentiate:
    // - Page requests (Accept: text/html) -> serve index.html for React Router
    // - API requests (Accept: application/json) -> proxy to backend
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    this.baseUrl = baseUrl || (isDevelopment ? (process.env.REACT_APP_SERVER_URL ) : process.env.REACT_APP_SERVER_URL || "");
    this.authApiUrl = process.env.REACT_APP_AUTH_API_URL || "";
    this.withCredentials = withCredentials;

    // Activity-based token refresh configuration
    // These settings work globally across all modules (Kanban, Defect Management, Test Cases, etc.)
    this.activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "framecommand"];
    this.boundHandleActivity = this.handleUserActivity.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.lastActivityReset = 0;
    this.activityResetThrottle = 60000; // Only reset timer once per minute to avoid excessive resets
    this.isActivityTrackingEnabled = false; // Disabled by default, enabled after login

    // Tag instance so interceptors know whether to trigger token refresh on 401.
    // Non-auth services (agent-server, license-server) should NOT trigger
    // refresh/logout when they return 401 — their auth is independent.
    (this.instance as any)._skipAuthInterceptor = skipAuthInterceptor;
    setupInterceptors(this.instance);
  }

  static getInstance(): ApiService {
    if (ApiService.myInstance === null) {
      ApiService.myInstance = new ApiService();
    }
    return ApiService.myInstance;
  }

  get<T = any>(path: string = "", params: Record<string, any> = {}, options?: RequestOptions): Promise<ApiResponseData<T>> | null {
    try {
      const config = this.getConfig(params);
      if (options?.backgroundPoll) {
        (config as any)._backgroundPoll = true;
      }
      return this.instance.get(`${this.baseUrl}${path}`, config);
    } catch (e) {
      logger.error('API_SERVICE', 'GET request error', e);
      return null;
    }
  }

  getFile(path: string = "", params: Record<string, any> = {}, data: any = null,
          timeoutMs?: number): Promise<AxiosResponse<Blob>> {
    let config = this.getConfig(params);
    config.responseType = "blob";
    // Optional per-call timeout override — the shared axios instance is
    // capped at 30s, which is too short for server-side aggregations like
    // the executive PDF (multi-section join over a full month of runs +
    // defects + perf can legitimately run 45-120s on a busy project).
    if (timeoutMs && timeoutMs > 0) {
      config.timeout = timeoutMs;
    }
    if (data) {
      return this.instance.post(`${this.baseUrl}${path}`, data, config);
    } else {
      return this.instance.get(`${this.baseUrl}${path}`, config);
    }
  }

  postGetFile(path: string = "", data: any): Promise<AxiosResponse<Blob>> {
    let config = this.getConfig({});
    config.responseType = "blob";
    return this.instance.post(`${this.baseUrl}${path}`, data, config);
  }

  signout<T = any>(path: string = "", data: any): Promise<ApiResponseData<T>> {
    return this.instance.post(`${this.baseUrl}${path}`, data, this.getConfig());
  }

  post<T = any>(path: string = "", data: any, headers?: AxiosRequestConfig): Promise<ApiResponseData<T>> {
    return this.instance.post(`${this.baseUrl}${path}`, data, headers ? headers : this.getConfig());
  }

  postWithTimeout<T = any>(path: string, data: any, timeoutMs: number): Promise<ApiResponseData<T>> {
    const config = this.getConfig();
    config.timeout = timeoutMs;
    return this.instance.post(`${this.baseUrl}${path}`, data, config);
  }

  postFile<T = any>(path: string, formData: FormData): Promise<ApiResponseData<T>> {
    return this.instance.post(`${this.baseUrl}${path}`, formData, this.getConfig());
  }

  put<T = any>(path: string = "", data: any, options?: RequestOptions): Promise<ApiResponseData<T>> {
    const config = this.getConfig();
    if (options?.source) {
      (config as any)._requestSource = options.source;
    }
    return this.instance.put(`${this.baseUrl}${path}`, data, config);
  }

  patch<T = any>(path: string = "", data: any, options?: RequestOptions): Promise<ApiResponseData<T>> {
    const config = this.getConfig();
    if (options?.source) {
      (config as any)._requestSource = options.source;
    }
    return this.instance.patch(`${this.baseUrl}${path}`, data, config);
  }

  delete<T = any>(path: string, options?: { data?: any }): Promise<ApiResponseData<T>> {
    const config = this.getConfig();
    if (options?.data) {
      (config as any).data = options.data;
    }
    return this.instance.delete(`${this.baseUrl}${path}`, config);
  }

  getConfig(params: Record<string, any> = {}): AxiosRequestConfig {
    const selectedBranch = localStorage.getItem("selectedBranch") || "local";
    const token = AuthService.token;
    const refreshToken = AuthService.refreshToken;

    // Removed verbose logging - getConfig is called on every API request
    // Too frequent for console logging

    const config: AxiosRequestConfig = {
      params
    };

    if (this.withCredentials) {
      // Get CSRF token if available (requires backend support)
      let csrfToken: string | null = null;
      try {
        const { getCSRFToken } = require('../utils/security');
        csrfToken = getCSRFToken();
      } catch (e) {
        // CSRF not implemented yet
      }

      config.headers = {
        Authorization: "Bearer " + (token || ""),
        Accesstoken: token || "",
        Refreshtoken: refreshToken || "",
        selectedBranch: selectedBranch,
        // Add CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
        // Backend must support this - otherwise it will be ignored
        ...(csrfToken && { 'X-CSRF-Token': csrfToken, 'X-Requested-With': 'XMLHttpRequest' })
      };

      // Removed verbose logging - headers are set on every API request
    }

    return config;
  }

  /**
   * Activity-based token refresh: Reset timer on user activity
   * This works across ALL modules - Kanban, Defect Management, Test Cases, etc.
   * Any user interaction (click, type, scroll) will keep the session alive.
   */
  private handleUserActivity(): void {
    if (!this.isActivityTrackingEnabled) {
      return; // Activity tracking not enabled yet
    }

    const token = AuthService.token;
    if (!token) {
      return;
    }

    // Throttle: Only reset timer once per minute to avoid excessive timer resets
    const now = Date.now();
    if (now - this.lastActivityReset < this.activityResetThrottle) {
      return; // Too soon, throttle this activity
    }

    this.lastActivityReset = now;
    this.startRefreshTokenTimer();
  }

  /**
   * Handle page visibility change (user switches back to tab)
   * Check if token is expired and trigger immediate refresh if needed.
   * This prevents 401 cascades when returning from a background tab where
   * setTimeout timers were throttled by the browser.
   */
  private handleVisibilityChange(): void {
    if (!this.isActivityTrackingEnabled) {
      return;
    }

    if (!document.hidden) {
      // Tab became visible — check token health immediately
      const token = AuthService.token;
      if (token) {
        try {
          const decoded = jwtDecode<JWTPayload>(token);
          const expiresIn = decoded.exp * 1000 - Date.now();

          if (expiresIn < 60000) {
            // Token expired or about to expire — trigger immediate refresh
            // This runs BEFORE React re-renders trigger API calls
            logger.warn('API', 'Tab visible — token expired/expiring, triggering immediate refresh', {
              expiresInMs: expiresIn
            });
            getRefreshToken(); // Guarded by isRefreshing — safe to call
          }
        } catch (e) {
          // Token decode error — let normal flow handle it
        }

        this.lastActivityReset = Date.now(); // Reset throttle timer
        this.startRefreshTokenTimer();
      }
    }
  }

  /**
   * Setup activity listeners to reset token refresh timer
   * This enables activity-based session management across ALL modules.
   * Call this method once after successful login.
   *
   * The listeners work globally and will keep the session alive regardless of which
   * module the user is using (Kanban, Defect Management, Test Cases, etc.)
   */
  setupActivityListeners(): void {
    if (this.isActivityTrackingEnabled) {
      return;
    }

    // Add user interaction listeners — use capture phase so events are detected
    // even when stopPropagation() is called (e.g., inside flow pane drag library)
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.boundHandleActivity, { capture: true, passive: true });
    });

    // Add visibility change listener (when user switches back to tab)
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);

    // Poll for iframe focus — cross-origin iframes (e.g., Component tab page preview)
    // don't fire DOM events on the parent document, but we can detect that an iframe
    // is the active element, which means the user is interacting inside it
    this.iframeFocusIntervalId = setInterval(() => {
      const activeEl = document.activeElement;
      if (activeEl && activeEl.tagName === 'IFRAME') {
        this.handleUserActivity();
      }
    }, 2000);

    this.isActivityTrackingEnabled = true;
  }

  /**
   * Clean up activity listeners
   * Call this method on logout to prevent memory leaks and unnecessary processing.
   */
  cleanupActivityListeners(): void {
    if (!this.isActivityTrackingEnabled) {
      return;
    }

    // Remove user interaction listeners (must match capture phase used in setup)
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.boundHandleActivity, { capture: true } as EventListenerOptions);
    });

    // Clear iframe focus poller
    if (this.iframeFocusIntervalId) {
      clearInterval(this.iframeFocusIntervalId);
      this.iframeFocusIntervalId = null;
    }

    // Remove visibility change listener
    document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);

    this.isActivityTrackingEnabled = false;
    this.lastActivityReset = 0; // Reset the throttle timer
  }

  startRefreshTokenTimer(): void {
    if (refreshTokenTimeout) {
      clearTimeout(refreshTokenTimeout);
    }
    const token = AuthService.token;
    if (!token) {
      return;
    }

    let decodedToken: JWTPayload;
    try {
      decodedToken = jwtDecode<JWTPayload>(token);
    } catch (e) {
      // Token is invalid (not a valid JWT) — try to recover via refresh token
      // instead of immediately clearing and forcing logout.
      logger.error('API', 'Invalid access token in startRefreshTokenTimer', { error: e });
      if (AuthService.refreshToken) {
        logger.info('API', 'Refresh token exists — attempting recovery via token refresh');
        localStorage.removeItem('accessToken');
        getRefreshToken();
      } else {
        logger.error('API', 'No refresh token — clearing invalid access token');
        AuthService.setLogoutReason('Invalid access token detected — cleared automatically');
        localStorage.removeItem('accessToken');
      }
      return;
    }
    const oneMinute = 6e4;
    const refreshTokenDelay = decodedToken.exp * 1e3 - new Date().getTime();
    const hasRefreshToken = !!AuthService.refreshToken;

    if (refreshTokenDelay < oneMinute) {
      // Loop-breaker: if a refresh succeeded within the last few seconds, don't
      // turn around and fire another one. The setAuthToken PubSub subscriber
      // calls startRefreshTokenTimer right after every successful refresh, and
      // a transient cross-document race can make this read see a near-expiry
      // token even though we JUST set a 5h-TTL one — without this guard the
      // cycle is success → publish → subscriber → immediate refresh → loop.
      // Returning WITHOUT scheduling is intentional: a re-scheduled setTimeout
      // with the same near-expiry token would just have a negative delay,
      // fire immediately, and re-establish the loop via the timer instead of
      // the direct call. The existing safety nets — preflight check on the
      // next API request, activity-reset re-call (line 357), and visibility
      // change (line 392) — will trigger a fresh evaluation soon enough.
      const sinceLastSuccess = Date.now() - lastSuccessfulRefreshAt;
      if (lastSuccessfulRefreshAt > 0 && sinceLastSuccess < 5000) {
        console.warn(
          '%c[REFRESH TIMER] Skipping immediate refresh — last success was %dms ago (loop guard). Not scheduling; preflight / activity / visibility will re-trigger.',
          'background: #a50; color: #fff; font-size: 12px; padding: 2px 6px;',
          sinceLastSuccess,
          { expiresInMs: refreshTokenDelay, hasRefreshToken }
        );
        // 📊 DIAGNOSTIC: "loop guard fired AND token is still expired" is a paradox —
        // we just successfully refreshed Xms ago but AuthService.token is still showing
        // negative expiresInMs. This means either:
        //   • AuthService.token setter silently rejected the new token (see auth.service.ts
        //     setter — should now log via 'Token setter rejected new token' warn)
        //   • The server issued an already-expired access_token (clock skew, or RT-bounded TTL)
        // Throttled to once per 10s so we don't drown the console during a spin.
        if (refreshTokenDelay < 0) {
          const now = Date.now();
          if (now - (lastLoopParadoxLogAt || 0) > 10000) {
            lastLoopParadoxLogAt = now;
            console.error(
              '%c[REFRESH TIMER] ⚠️ Refresh succeeded %dms ago but token still expired by %ds — likely setter rejection (look for "Token setter rejected new token") OR server-issued expired token. Decode AuthService.token JWT (jwt.io) and compare exp to Date.now().',
              'background: #a00; color: #fff; font-size: 13px; padding: 3px 8px;',
              sinceLastSuccess,
              Math.round(-refreshTokenDelay / 1000)
            );
          }
        }
        return;
      }
      console.log(
        '%c[REFRESH TIMER] Token expired/expiring — triggering immediate refresh',
        'background: #a50; color: #fff; font-size: 12px; padding: 2px 6px;',
        { expiresInMs: refreshTokenDelay, hasRefreshToken }
      );
      if (!hasRefreshToken) {
        console.error(
          '%c[REFRESH TIMER] ⚠️ No refresh token available — refresh WILL fail!',
          'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;'
        );
      }
      getRefreshToken();
    } else {
      console.log(
        `%c[REFRESH TIMER] Token valid for ${Math.round(refreshTokenDelay / 60000)}min — refresh scheduled in ${Math.round((refreshTokenDelay - oneMinute) / 60000)}min`,
        'color: #0a0;',
        { hasRefreshToken }
      );
      refreshTokenTimeout = setTimeout(() => {
        console.log(
          '%c[REFRESH TIMER] Proactive refresh timer fired',
          'background: #06a; color: #fff; font-size: 12px; padding: 2px 6px;',
          { hasRefreshToken: !!AuthService.refreshToken, time: new Date().toISOString() }
        );
        getRefreshToken();
      }, refreshTokenDelay - oneMinute);
    }
  }
}

// ============================================================================
// NETWORK RESILIENCE HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an error is retryable (network-related)
 */
function isNetworkErrorRetryable(error: AxiosError): boolean {
  // No response means network issue
  if (!error.response) {
    // Check for specific error types
    if (error.code === 'ECONNABORTED') return true; // Timeout
    if (error.code === 'ETIMEDOUT') return true;
    if (error.code === 'ECONNRESET') return true;
    if (error.message === 'Network Error') return true;

    // Check message patterns
    const message = error.message?.toLowerCase() || '';
    return NETWORK_CONFIG.RETRYABLE_ERRORS.some(
      errType => message.includes(errType.toLowerCase())
    );
  }

  // Server errors (5xx) are retryable
  if (error.response.status >= 500 && error.response.status < 600) {
    return true;
  }

  return false;
}

/**
 * Start periodic health check when connection issues are detected
 */
function startConnectionHealthCheck(instance: AxiosInstance): void {
  // Don't start multiple health checks
  if (healthCheckInterval) return;

  connectionHealthy = false;
  logger.warn('API', 'Starting connection health check monitoring');

  const checkHealth = async () => {
    const now = Date.now();
    // Throttle health checks
    if (now - lastHealthCheckTime < NETWORK_CONFIG.HEALTH_CHECK_INTERVAL / 2) return;
    lastHealthCheckTime = now;

    try {
      const baseUrl = process.env.REACT_APP_SERVER_URL || '';
      const healthUrl = `${baseUrl}${NETWORK_CONFIG.HEALTH_CHECK_ENDPOINT}`;

      // Simple health check with short timeout
      await axios.get(healthUrl, {
        timeout: 5000,
        validateStatus: (status) => status < 500 // Accept any non-5xx response
      });

      // Connection restored
      connectionHealthy = true;
      consecutiveFailures = 0;
      logger.info('API', 'Connection restored - health check passed');
      toast.success('Connection restored');

      // Stop health check monitoring
      stopConnectionHealthCheck();
    } catch (e) {
      consecutiveFailures++;
      logger.warn('API', `Health check failed (attempt ${consecutiveFailures})`, {
        error: (e as Error).message
      });

      // After many consecutive failures, reduce check frequency
      if (consecutiveFailures > 10) {
        logger.warn('API', 'Multiple health check failures - server may be down');
      }
    }
  };

  // Run initial check
  checkHealth();

  // Set up periodic checks
  healthCheckInterval = setInterval(checkHealth, NETWORK_CONFIG.HEALTH_CHECK_INTERVAL);
}

/**
 * Stop health check monitoring (called when connection is restored)
 */
function stopConnectionHealthCheck(): void {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
    healthCheckInterval = null;
    logger.info('API', 'Stopped connection health check monitoring');
  }
}

/**
 * Check if connection is currently healthy
 */
export function isConnectionHealthy(): boolean {
  return connectionHealthy;
}

/**
 * Get consecutive failure count
 */
export function getConsecutiveFailures(): number {
  return consecutiveFailures;
}

function setupInterceptors(instance: AxiosInstance = axios): void {
  createRequestInterceptor(instance);
  createResponseInterceptor(instance);
}

let isRefreshing = false;
let subscribers: TokenRefreshCallback[] = [];
let failedSubscribers: ((error: any) => void)[] = [];
let refreshTokenAttempts = 0;
let maxRefreshTokenAttempts = 5;
let emptyResponseRetries = 0; // Separate counter for empty access_token retries
let isSendErrorMsg = false;

/**
 * Security: Validate and sanitize request data for injection attacks
 * This provides defense-in-depth even if browser extension sanitization is bypassed
 */
function validateRequestData(data: any, path: string): { valid: boolean; sanitizedData: any; threats: string[] } {
  if (!data || typeof data !== 'object') {
    return { valid: true, sanitizedData: data, threats: [] };
  }

  const threats: string[] = [];

  // Fields that commonly contain user input from captured webpages
  const sensitiveFields = [
    'xpath', 'cssPath', 'selector', 'id', 'name', 'value', 'innerText', 'innerHTML',
    'query', 'sqlQuery', 'sql', 'command', 'script', 'expression', 'filter',
    'url', 'href', 'src', 'action', 'path', 'filename'
  ];

  // Fields that hold programmatically-generated CSS / XPath selectors. Their
  // syntax (e.g. `[class*="-singleValue"]`, `//*[@id='state']`) trips the
  // general injection scanner's XPath-traversal and attribute-selector rules
  // as false positives. These values are built by our own path-builder code,
  // not user input, so they're safe to skip.
  const selectorFieldAllowlist = new Set([
    'fulldompath',
    'csspath',
    'xpath',
    'rxpath',
    'axpath',
    'stablecss',
    'stablexpath',
    // The Smart Locator stable recognizer fields travel on activities at the
    // top level (not just inside a path object). Their contents are xpath /
    // css produced by our own path builder, so the injection scanner must
    // not flag them.
    'stablerecognizer',
    'stablerecognizerby',
    'recognizer',
    'selector',
    'dompaths',
    // per-activity alternate locators
    'alternativelocators',
    'alternativexpath',
    'alternativecss',
    // Crawler payloads carry CSS selectors + hrefs on every interactive
    // element (`input[type="password"]`, `a[href="/cart"]`, …). Without
    // these entries, a single 30-page crawl fires thousands of "attribute
    // selector attack" WARNs — cosmetic but drowns the real console output.
    'cssselector',
    'href',
    'containerclasses'
  ]);

  function scanValue(key: string, value: any): any {
    if (typeof value !== 'string') return value;

    // Skip base64 encoded data (screenshots, files)
    if (value.startsWith('data:') || value.length > 50000) return value;

    // Skip programmatic selector fields — they legitimately contain CSS
    // attribute selectors and XPath predicates that look like XPath
    // injection to the generic scanner.
    if (selectorFieldAllowlist.has(key.toLowerCase())) return value;

    // Determine context for more accurate detection
    const context = key.toLowerCase().includes('query') || key.toLowerCase().includes('sql')
      ? 'query'
      : key.toLowerCase().includes('url') || key.toLowerCase().includes('href')
      ? 'url'
      : 'general';

    const result = detectInjections(value, context);

    if (!result.isSafe) {
      const criticalThreats = result.threats.filter(t => t.severity === 'critical' || t.severity === 'high');
      if (criticalThreats.length > 0) {
        threats.push(`Field "${key}": ${criticalThreats.map(t => t.type).join(', ')}`);
        logger.warn('API_SECURITY', `⚠️ Injection attempt detected in field "${key}"`, {
          field: key,
          severity: result.severity,
          threats: criticalThreats.map(t => ({ type: t.type, desc: t.description }))
        });
      }
    }

    return value; // Return original - backend should do final validation
  }

  function scanObject(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
      return obj.map((item, idx) => typeof item === 'object' ? scanObject(item) : scanValue(`[${idx}]`, item));
    }

    const scanned: any = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (typeof value === 'object' && value !== null) {
        scanned[key] = scanObject(value);
      } else if (sensitiveFields.some(f => key.toLowerCase().includes(f.toLowerCase()))) {
        scanned[key] = scanValue(key, value);
      } else {
        scanned[key] = value;
      }
    }
    return scanned;
  }

  const sanitizedData = scanObject(data);

  // Log summary if threats found
  if (threats.length > 0) {
    logger.warn('API_SECURITY', `🛡️ Security scan complete for ${path}`, {
      threatCount: threats.length,
      threats
    });
  }

  return { valid: true, sanitizedData, threats };
}

function createRequestInterceptor(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Pre-flight token expiry check: if the access token is expired or about to expire,
      // trigger a refresh and wait for it before sending the request.
      // This prevents 401 cascades when returning from a background tab.
      // Skip for non-auth instances — their 401s are handled independently.
      const skipAuth = (instance as any)._skipAuthInterceptor;
      const token = AuthService.token;
      const url = config.url || '';
      // The refresh endpoint moved from `/auth/refresh` to `/apis/refresh` (see
      // AUTO_LOGOUT.md — March 2026 fix). This check was missed in that move,
      // which meant pre-flight tried to refresh-before-sending on the refresh
      // request itself — wasteful, and a potential deadlock when the access
      // token has <30s of life left (the case where pre-flight kicks in).
      // Match either spelling so any future endpoint rename is caught too.
      const isRefreshRequest = url.includes('/auth/refresh') || url.includes('/apis/refresh');

      if (token && !isRefreshRequest && !skipAuth) {
        try {
          const decoded = jwtDecode<JWTPayload>(token);
          const expiresIn = decoded.exp * 1000 - Date.now();

          if (expiresIn < 30000) { // Less than 30 seconds to expiry or already expired
            logger.warn('API', 'Token expired/expiring — refreshing before request', {
              url: config.url,
              expiresInMs: expiresIn
            });

            // Wait for token refresh to complete before sending this request
            const newToken = await new Promise<string>((resolve, reject) => {
              subscribeTokenRefresh(resolve);
              failedSubscribers.push(reject);
              getRefreshToken(); // Guarded by isRefreshing — safe to call multiple times
            });

            // Update request headers with the fresh token
            if (config.headers) {
              config.headers.Authorization = `Bearer ${newToken}`;
              config.headers.Accesstoken = newToken;
            }
          }
        } catch (e: any) {
          // Token decode failed or refresh failed — let the request proceed,
          // the response interceptor will handle 401
          console.warn(
            '%c[PRE-FLIGHT] Token refresh failed — request will proceed with expired token',
            'background: #a50; color: #fff; font-size: 12px; padding: 2px 6px;',
            { url: config.url, error: e?.message || e, hasRefreshToken: !!AuthService.refreshToken }
          );
        }
      }

      // ⚠️ SECURITY: Scan request data for injection attacks
      // Data from our browser extension (page elements, XPath/CSS selectors) is trusted
      // and skips client-side scanning. The backend still validates all incoming data.
      const requestSource = (config as any)._requestSource;
      const isExtensionData = requestSource === 'extension';

      if (config.data && (config.method === 'post' || config.method === 'put' || config.method === 'patch')) {
        if (isExtensionData) {
          logger.info('API_SECURITY', '🛡️ Skipping injection scan — trusted extension data', {
            url: config.url,
            source: requestSource
          });
        } else {
          const { valid, sanitizedData, threats } = validateRequestData(config.data, config.url || '');

          // Log threats but don't block - let backend do final validation
          // This provides defense-in-depth logging
          if (threats.length > 0) {
            logger.warn('API_SECURITY', '⚠️ Request contains potential injection payloads', {
              url: config.url,
              method: config.method,
              threats
            });
          }
        }
      }

      // Clean up internal metadata before sending to server
      if ((config as any)._requestSource) {
        delete (config as any)._requestSource;
      }

      logger.network(config.method?.toUpperCase() || "GET", config.url || "", {
        headers: config.headers,
        params: config.params,
        data: config.data
      });
      return config;
    },
    (error: AxiosError) => {
      logger.error('API', 'Request error', error);
      return Promise.reject(error);
    }
  );
}

function createResponseInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponseData>) => {
      const originalRequest = response.config;
      triggerExtension(response);
      // Check for 401 errors embedded in 200 responses (backend wraps some errors this way)
      if (response.status === 401 || (response.data.error && response.data.error.code === 401)) {
        // If this instance is flagged to skip auth interceptor (non-auth server),
        // reject immediately — don't trigger token refresh / globalLogout.
        if ((instance as any)._skipAuthInterceptor) {
          logger.warn('API', '401 from non-auth server — skipping token refresh', { url: response.config.url });
          return Promise.reject({
            response: { status: 401, data: response.data },
            message: 'Unauthorized (non-auth server)',
            config: response.config
          });
        }

        const errorMsg = response.data?.error?.description || response.data?.error?.message || '';
        const isTokenExpiry = errorMsg.toLowerCase().includes('token') ||
                             errorMsg.toLowerCase().includes('expired') ||
                             errorMsg.toLowerCase().includes('invalid') ||
                             errorMsg.toLowerCase().includes('authentication') ||
                             errorMsg.toLowerCase().includes('unauthorized');

        // Attempt token refresh on 401 — if refresh succeeds, retry the request;
        // if refresh fails, the catch handler in getRefreshToken() will logout the user.
        logger.warn('API', `401 error - attempting token refresh`, {
          url: response.config.url,
          errorMsg,
          isTokenExpiry
        });
        return handleErrorResponse(originalRequest) as any;
      } else if (
        response.config.responseType !== undefined &&
        response.config.responseType === "blob" &&
        (response.data as any).size === 67
      ) {
        logger.warn('API', 'Blob response error detected');
        return handleErrorResponse(originalRequest) as any;
      }
      // Successful API response = app is active; reset auto-logout timer.
      // Skip background polling endpoints — they fire on intervals regardless of
      // user activity and would keep the session alive indefinitely.
      if (!isBackgroundPollRequest(response.config, response.config?.url)) {
        AutoLogout.getInstance()?.recordApiActivity();
      }

      return response.data as any;
    },
    async (error: AxiosError<ApiResponseData>) => {
      LoaderService.hide();

      // Enhanced error logging and user feedback
      const errorDetails = {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data,
        code: error.code
      };

      const originalRequest = error.config as InternalAxiosRequestConfig & { _retryCount?: number };

      // Check for retryable network errors
      const isRetryableError = isNetworkErrorRetryable(error);

      if (isRetryableError && originalRequest) {
        // Initialize retry count
        originalRequest._retryCount = originalRequest._retryCount || 0;

        if (originalRequest._retryCount < NETWORK_CONFIG.MAX_RETRIES) {
          originalRequest._retryCount++;
          consecutiveFailures++;

          // Calculate exponential backoff delay
          const delay = Math.min(
            NETWORK_CONFIG.RETRY_DELAY_BASE * Math.pow(2, originalRequest._retryCount - 1),
            NETWORK_CONFIG.RETRY_DELAY_MAX
          );

          logger.warn('API', `Network error - retrying (${originalRequest._retryCount}/${NETWORK_CONFIG.MAX_RETRIES})`, {
            url: originalRequest.url,
            delay,
            error: error.message
          });

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, delay));

          // Retry the request
          return instance(originalRequest);
        }

        // All retries exhausted - start health check
        logger.error('API', 'All retries exhausted', errorDetails);
        startConnectionHealthCheck(instance);
      }

      // Check for CORS/Network errors (after retries exhausted)
      if (error.message === 'Network Error' && !error.response) {
        logger.error('API', 'CORS or Network error detected', errorDetails);
        consecutiveFailures++;

        // Start health check monitoring
        startConnectionHealthCheck(instance);

        // Provide user-friendly message for CORS issues in development
        const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDevelopment) {
          toast.error('Network Error: Please ensure the backend server is running on localhost:8080');
        } else {
          toast.error('Network Error: Unable to connect to server. Retrying connection...');
        }
      } else if (error.code === 'ECONNABORTED') {
        logger.error('API', 'Request timeout', errorDetails);
        toast.error('Request timed out. Please try again.');
      } else {
        logger.error('API', 'Response error', errorDetails);
      }

      // Any API error still means the user's app is active — reset auto-logout timer.
      // This prevents logout during transient failures (500, 400, network hiccups).
      // Skip background polling endpoints — they fire on intervals regardless of
      // user activity and would keep the session alive indefinitely.
      if (error.response && error.response.status !== 401 && !isBackgroundPollRequest(error.config, error.config?.url)) {
        AutoLogout.getInstance()?.recordApiActivity();
      }

      // Handle 401 Unauthorized
      if (error.response && error.response.status === 401) {
        // Non-auth servers: reject locally, don't trigger token refresh / globalLogout
        if ((instance as any)._skipAuthInterceptor) {
          logger.warn('API', '401 from non-auth server — skipping token refresh', { url: error.config?.url });
          return Promise.reject(error);
        }
        return handleErrorResponse(originalRequest);
      }

      // Handle 404 Not Found (Resource Not Found)
      if (error.response && error.response.status === 404) {
        const requestUrl = error.config?.url || '';
        const resourceType = extractResourceType(requestUrl);
        const errorMessage = error.response.data?.error?.description ||
                            error.response.data?.error?.message ||
                            `${resourceType} not found`;

        // Suppress toast notifications for metrics endpoints (they may not be implemented yet)
        const isMetricsEndpoint = requestUrl.includes('/metrics');

        if (!isMetricsEndpoint) {
          logger.warn('API', `404 - ${resourceType} not found`, {
            url: requestUrl,
            message: errorMessage
          });

          // Show user-friendly toast notification (only for non-metrics endpoints)
          toast.warning(errorMessage);
        }

        // Return a structured error response instead of rejecting
        return Promise.resolve({
          success: false,
          error: {
            code: 404,
            description: errorMessage,
            message: errorMessage
          }
        } as ApiResponseData);
      }

      // Handle 400 Bad Request
      if (error.response && error.response.status === 400) {
        console.log('🔴 [API] 400 Bad Request intercepted:', {
          url: error.config?.url,
          data: error.response.data
        });
        const errorMessage = error.response.data?.error?.description ||
                            error.response.data?.error?.message ||
                            error.response.data?.detail ||
                            'Invalid request';
        const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
        const url = error.config?.url || 'unknown';

        console.log('🔴 [API] Error message extracted:', errorMessage);

        logger.warn('API', '400 - Bad Request', {
          method,
          url,
          message: errorMessage
        });

        // Suppress toast for known non-critical errors
        // PUT /boards/{boardId} fails with "Failed to read request" but board still works
        const isBoardUpdateError = method === 'PUT' && url.includes('/boards/') && errorMessage.includes('Failed to read request');

        if (!isBoardUpdateError) {
          // Show a cleaner error message - just the description without method/URL
          console.log('🔴 [API] Showing toast.error with message:', errorMessage);
          toast.error(errorMessage);
        } else {
          // Just log it for debugging, don't show toast
          console.warn(`⚠️ Suppressed non-critical error: ${method} ${url} - ${errorMessage}`);
        }

        // Return a structured error response instead of rejecting
        const response = {
          success: false,
          error: {
            code: 400,
            description: errorMessage,
            message: errorMessage
          }
        } as ApiResponseData;
        console.log('🔴 [API] Returning resolved response:', response);
        return Promise.resolve(response);
      }

      // Section-specific error logging — tag each API error with its module
      const url = error.config?.url || '';
      const status = error.response?.status || 0;
      const section = getApiSection(url);
      const errorMessage = error.response?.data?.error?.description ||
                          error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Unknown error';

      console.error(
        `%c[${section}] API ${status} — ${error.config?.method?.toUpperCase() || 'GET'} ${url}`,
        `background: #a00; color: #fff; font-size: 12px; padding: 2px 6px;`,
        { status, message: errorMessage, response: error.response?.data }
      );

      // Handle 500 Internal Server Error
      if (status === 500) {
        logger.error(section, `500 - ${url}`, errorMessage);
        toast.error(errorMessage);
      }

      return Promise.reject(error);
    }
  );
}

function handleErrorResponse(originalRequest: AxiosRequestConfig | InternalAxiosRequestConfig | undefined): Promise<any> {
  if (!originalRequest) {
    return Promise.reject("No original request");
  }

  // Trigger refresh if not already in progress (guard is inside getRefreshToken)
  getRefreshToken();

  return new Promise((resolve, reject) => {
    subscribeTokenRefresh(token => {
      if (!originalRequest.headers) {
        originalRequest.headers = {} as any;
      }
      (originalRequest.headers as any).Authorization = `Bearer ${token}`;
      resolve(axios(originalRequest as AxiosRequestConfig));
    });
    failedSubscribers.push(reject);
  }).then((res: any) => res.data);
}

/**
 * Cross-document refresh mutex. The in-memory `isRefreshing` guard only
 * serializes refreshes WITHIN one document — it doesn't stop the parent and
 * an embedded iframe (e.g. `/iframe`) from each firing a refresh in the same
 * window. When that happens, the second request carries the now-rotated token
 * and the backend returns 400 "Invalid Refresh Token — not found in database",
 * which logs the user out.
 *
 * `navigator.locks.request` is a same-origin mutex shared across the parent,
 * its iframes, and other tabs/windows, so only ONE refresh is ever in flight
 * for this user at a time. Falls back to running directly on browsers without
 * the Web Locks API (rare; supported in all evergreen browsers).
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const nav: any = typeof navigator !== 'undefined' ? navigator : undefined;
  if (nav && nav.locks && typeof nav.locks.request === 'function') {
    return nav.locks.request('atpt-refresh-token', { mode: 'exclusive' }, fn);
  }
  return fn();
}

/**
 * True when the stored access token still has at least `minSecondsLeft` of life.
 * Used after acquiring the refresh lock to detect that another context (the
 * other side of the iframe boundary) already did the refresh while we waited,
 * so we can skip the network call entirely.
 */
function isAccessTokenStillValid(minSecondsLeft: number): boolean {
  const token = AuthService.token;
  if (!token) return false;
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    return decoded.exp * 1000 - Date.now() > minSecondsLeft * 1000;
  } catch {
    return false;
  }
}

function getRefreshToken(): void {
  // Guard: prevent concurrent refresh requests (race condition fix)
  // Multiple callers (timer, 401 handler, visibility change) may call this simultaneously.
  // Only the first call proceeds; others return and wait for subscribers to be resolved.
  if (isRefreshing) {
    return;
  }
  isRefreshing = true;

  // Inner retry loop — keeps isRefreshing=true throughout all retries so concurrent
  // 401 handlers (e.g., daemon command polling) can't race and exhaust attempts early.
  function attemptRefresh(): void {
    let refreshToken = AuthService.refreshToken;

    // Recovery: try sessionStorage backup if localStorage is empty
    if (!refreshToken) {
      const backup = sessionStorage.getItem('__refresh_token_backup');
      if (backup) {
        console.warn(
          '%c[TOKEN REFRESH] Recovering refresh token from sessionStorage backup',
          'background: #a50; color: #fff; font-size: 13px; padding: 2px 6px;'
        );
        localStorage.setItem('refreshToken', backup);
        refreshToken = backup;
      }
    }

    if (!refreshToken) {
      if (refreshTokenAttempts < maxRefreshTokenAttempts) {
        refreshTokenAttempts++;
        const retryInfo = {
          attempt: refreshTokenAttempts,
          max: maxRefreshTokenAttempts,
          hasAccessToken: !!AuthService.token,
          tokenKeys: Object.keys(localStorage).filter(k => k.toLowerCase().includes('token')),
          pendingSubscribers: subscribers.length,
        };
        console.warn(
          `%c[TOKEN REFRESH] No refresh token — retry ${refreshTokenAttempts}/${maxRefreshTokenAttempts}`,
          'background: #a50; color: #fff; font-size: 12px; padding: 2px 6px;',
          retryInfo
        );
        logger.info('API', `No refresh token in localStorage — retry ${refreshTokenAttempts}/${maxRefreshTokenAttempts}`, retryInfo);
        // Persist to sessionStorage so it survives the redirect
        try {
          const log = JSON.parse(sessionStorage.getItem('__token_event_log') || '[]');
          log.push({ event: `REFRESH_RETRY_${refreshTokenAttempts}`, time: new Date().toISOString(), ...retryInfo });
          sessionStorage.setItem('__token_event_log', JSON.stringify(log));
        } catch { /* ok */ }
        // Keep isRefreshing=true so concurrent callers queue up instead of racing
        setTimeout(attemptRefresh, 1000);
        return;
      }

      // All retries exhausted — no refresh token found
      const exhaustedInfo = {
        attempts: refreshTokenAttempts,
        hasAccessToken: !!AuthService.token,
        sessionStart: sessionStorage.getItem('__session_start'),
        tokenKeys: Object.keys(localStorage).filter(k => k.toLowerCase().includes('token') || k.toLowerCase().includes('refresh')),
        pendingSubscribers: subscribers.length,
        url: window.location.href,
      };
      console.error(
        '%c[TOKEN REFRESH] ALL RETRIES EXHAUSTED — no refresh token found, will logout',
        'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;',
        exhaustedInfo
      );
      logger.error('API', 'No refresh token after all retries', exhaustedInfo);
      try {
        const log = JSON.parse(sessionStorage.getItem('__token_event_log') || '[]');
        log.push({ event: 'REFRESH_RETRIES_EXHAUSTED', time: new Date().toISOString(), ...exhaustedInfo });
        sessionStorage.setItem('__token_event_log', JSON.stringify(log));
      } catch { /* ok */ }
      isRefreshing = false;
      refreshTokenAttempts = 0;
      rejectPendingSubscribers('No refresh token available');

      // If there's an access token (user was previously logged in), log them out
      // Skip logout only if there's no access token either (initial page load before login)
      if (AuthService.token) {
        AuthService.setLogoutReason('Session expired — no refresh token available');
        toast.info('Your session has expired. Please log in again.', { autoClose: 3000 });
        AuthService.globalLogout();
      }
      return;
    }

    // Refresh token found — proceed with refresh
    refreshTokenAttempts = 0;

    // Use the local auth endpoint (already incorporated into uiFlowBackEnd)
    // Previously proxied through AuthProxyController to external SSO server — now handled directly
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    const refreshUrl = isDevelopment ? '/api/apis/refresh' : process.env.REACT_APP_SERVER_URL + "/apis/refresh";

    logger.info('API', 'Sending token refresh request', { url: refreshUrl });

    withRefreshLock(() => {
      // Inside the cross-document lock. Another context (parent/iframe/tab)
      // may have refreshed while we were queued — if our access token is now
      // fresh, skip the POST and notify subscribers with the existing token.
      if (isAccessTokenStillValid(30)) {
        console.info(
          '%c[TOKEN REFRESH] Skipping POST — another context already refreshed (lock-coordinated)',
          'background: #050; color: #fff; font-size: 12px; padding: 2px 6px;'
        );
        isRefreshing = false;
        refreshTokenAttempts = 0;
        emptyResponseRetries = 0;
        const skippedToken = AuthService.token;
        if (skippedToken) onRefreshed(skippedToken);
        subscribers = [];
        failedSubscribers = [];
        return Promise.resolve();
      }
      // Re-read the refresh token under the lock — another context may have
      // rotated it after we captured `refreshToken` but before the lock was
      // acquired. Using the latest value avoids submitting a now-stale token.
      const freshRefreshToken = AuthService.refreshToken || refreshToken;
      return axios
      .post<RefreshTokenResponse>(refreshUrl, { refreshToken: freshRefreshToken })
      .then(res => {
        const { data } = res;

        // Detect backend error wrapper: {success: false, error: {code: 500, ...}}
        // The backend may return HTTP 200 but with an error payload (e.g., PatternParseException).
        // Treat this as a transient server error — retry instead of logging out.
        if ((data as any)?.success === false && (data as any)?.error) {
          const serverError = (data as any).error;
          console.error(
            '%c[TOKEN REFRESH] Backend returned error wrapper in 200 response!',
            'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;',
            { code: serverError.code, description: serverError.description, status: res.status }
          );
          logger.error('API', 'Token refresh got backend error wrapper', { code: serverError.code, description: serverError.description });
          // Retry — this is a server-side issue, not an auth issue
          if (emptyResponseRetries < 2) {
            emptyResponseRetries++;
            console.warn(
              `%c[TOKEN REFRESH] Server error — retrying in 2s (attempt ${emptyResponseRetries})`,
              'background: #a50; color: #fff; font-size: 12px; padding: 2px 6px;'
            );
            setTimeout(() => attemptRefresh(), 2000);
            return;
          }
          // Retries exhausted — but DON'T logout. The server is broken, not the user's session.
          // Reject pending subscribers so API calls fail gracefully.
          isRefreshing = false;
          emptyResponseRetries = 0;
          rejectPendingSubscribers('Server error during token refresh');
          toast.error('Server is temporarily unavailable. Please try again in a moment.', { autoClose: 5000 });
          return;
        }

        // Verify the server actually returned both tokens
        if (!data.access_token) {
          console.error(
            '%c[TOKEN REFRESH] Response MISSING access_token!',
            'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;',
            {
              keys: Object.keys(data),
              dataType: typeof data,
              dataPreview: JSON.stringify(data).substring(0, 500),
              status: res.status,
              headers: res.headers,
              hasPayload: !!(data as any)?.payload,
              payloadAccessToken: (data as any)?.payload?.access_token,
            }
          );
          // The server may have wrapped the response in { success, payload }
          // or returned a different structure — try to extract token from nested payload
          const nestedToken = (data as any)?.payload?.access_token || (data as any)?.accessToken;
          if (nestedToken) {
            console.warn('%c[TOKEN REFRESH] Found access_token in nested payload — using it', 'background: #a50; color: #fff;');
            data.access_token = nestedToken;
            data.refresh_token = data.refresh_token || (data as any)?.payload?.refresh_token || (data as any)?.refreshToken;
          } else {
            // Retry up to 3 times before giving up — transient server issues (proxy error,
            // empty response body, auth server hiccup) can cause this without the session being invalid
            if (emptyResponseRetries < 3) {
              emptyResponseRetries++;
              const delay = emptyResponseRetries * 2000; // 2s, 4s, 6s backoff
              console.warn(
                '%c[TOKEN REFRESH] Empty access_token — retrying in ' + (delay/1000) + 's (attempt ' + emptyResponseRetries + '/3)',
                'background: #a50; color: #fff; font-size: 12px; padding: 2px 6px;'
              );
              // Keep isRefreshing=true so concurrent callers queue instead of racing
              setTimeout(() => attemptRefresh(), delay);
              return;
            }
            logger.error('API', 'Refresh response missing access_token after retries', {
              keys: Object.keys(data), dataPreview: JSON.stringify(data).substring(0, 300)
            });
            isRefreshing = false;
            refreshTokenAttempts = 0;
            emptyResponseRetries = 0;

            // Check if the current access token is still valid before forcing logout
            // The auth server may be temporarily down — don't logout if we still have a working token
            const currentToken = AuthService.token;
            let tokenStillValid = false;
            if (currentToken) {
              try {
                const decoded = jwtDecode<JWTPayload>(currentToken);
                const expiresIn = decoded.exp * 1000 - Date.now();
                tokenStillValid = expiresIn > 0;
                console.warn(
                  `%c[TOKEN REFRESH] Current access token ${tokenStillValid ? 'still valid' : 'EXPIRED'} (${Math.round(expiresIn/1000)}s remaining)`,
                  `background: ${tokenStillValid ? '#060' : '#a00'}; color: #fff; font-size: 12px; padding: 2px 6px;`
                );
              } catch {
                // Can't decode — treat as expired
              }
            }

            if (tokenStillValid) {
              // Token still valid — don't logout, just warn and schedule another refresh attempt later
              rejectPendingSubscribers('Token refresh failed but access token still valid');
              toast.warning('Unable to renew session — will retry shortly.', { autoClose: 3000 });
              // Schedule another refresh attempt in 30 seconds
              setTimeout(() => {
                console.log('%c[TOKEN REFRESH] Retrying refresh after previous failure', 'background: #06a; color: #fff;');
                getRefreshToken();
              }, 30000);
            } else {
              // Token expired AND refresh failed — must logout
              rejectPendingSubscribers('Token refresh returned empty access_token');
              AuthService.setLogoutReason('Token refresh returned empty access_token');
              toast.info('Your session has expired. Please log in again.', { autoClose: 3000 });
              AuthService.globalLogout();
            }
            return;
          }
        }
        if (!data.refresh_token) {
          console.error(
            '%c[TOKEN REFRESH] Response MISSING refresh_token — next refresh WILL fail!',
            'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;',
            { keys: Object.keys(data), dataPreview: JSON.stringify(data).substring(0, 200) }
          );
          logger.error('API', 'Refresh response missing refresh_token — next refresh will fail!', { keys: Object.keys(data) });
        } else {
          console.log(
            '%c[TOKEN REFRESH] ✅ Tokens refreshed successfully',
            'background: #080; color: #fff; font-size: 12px; padding: 2px 6px;',
            { time: new Date().toISOString() }
          );
          try {
            const log = JSON.parse(sessionStorage.getItem('__token_event_log') || '[]');
            log.push({ event: 'REFRESH_SUCCESS', time: new Date().toISOString() });
            sessionStorage.setItem('__token_event_log', JSON.stringify(log));
          } catch { /* ok */ }
        }

        isRefreshing = false;
        refreshTokenAttempts = 0; // Reset on successful token receipt
        emptyResponseRetries = 0;
        // Record success time so the timer's immediate-refresh path can suppress
        // a self-amplifying loop via the setAuthToken PubSub subscriber.
        lastSuccessfulRefreshAt = Date.now();
        // Store tokens BEFORE notifying subscribers — subscribers and PubSub
        // handlers may read AuthService.token from localStorage, so it must
        // be up-to-date before they execute.
        AuthService.token = data.access_token;
        AuthService.refreshToken = data.refresh_token;
        onRefreshed(data.access_token);

        // Verify refresh token was actually persisted (setter may reject if decode fails)
        const storedRefresh = localStorage.getItem('refreshToken');
        if (!storedRefresh && data.refresh_token) {
          console.error(
            '%c[TOKEN REFRESH] Refresh token returned but NOT in localStorage — forcing storage!',
            'background: #a00; color: #fff; font-size: 13px; padding: 3px 6px;'
          );
          logger.error('API', 'Refresh token was returned but NOT stored — forcing storage');
          localStorage.setItem('refreshToken', data.refresh_token);
        } else if (!storedRefresh && !data.refresh_token) {
          console.error(
            '%c[TOKEN REFRESH] ⚠️ No refresh token in response AND none in localStorage!',
            'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;'
          );
        }

        // Update sessionStorage backup with the new refresh token — if the server
        // rotates refresh tokens, the backup must stay current for recovery after
        // page navigation or reload.
        if (data.refresh_token) {
          sessionStorage.setItem('__refresh_token_backup', data.refresh_token);
        }

        // Clear FORCE_LOGOUT flag on successful token refresh
        AuthService.clearForceLogoutFlag();
        PubSub.publish(eventNames.setAuthToken, data);
        subscribers = [];
        failedSubscribers = [];
      })
      .catch(error => {
        // 4xx from auth server = token is genuinely rejected (expired / rotated
        // away by another tab/iframe / not found in DB) — logout immediately.
        // Previously only 401 fast-failed; a 400 (which is what /apis/refresh
        // actually returns when the token is missing or invalid) fell into the
        // retry block below and wasted 3 × 2–6s retries sending the SAME bad
        // token before logging out anyway. Includes the server's message in the
        // logout reason so the next diagnosis is one log line away.
        const status = error.response && error.response.status;
        if (status === 400 || status === 401) {
          isRefreshing = false;
          rejectPendingSubscribers(error);
          if (AuthService.token) {
            const serverMsg = error.response?.data?.message
              || error.response?.data?.error?.description
              || '';
            const reasonDetail = status === 401
              ? 'Refresh token expired (401 from server)'
              : `Refresh token rejected (400 from server${serverMsg ? `: ${serverMsg}` : ''})`;
            AuthService.setLogoutReason(reasonDetail);
            toast.info('Your session has expired. Please log in again.', { autoClose: 3000 });
            AuthService.globalLogout();
          }
          return;
        }

        // Non-401 errors (network timeout, 500, connection refused) — retry before logout
        if (emptyResponseRetries < 3) {
          emptyResponseRetries++;
          const delay = emptyResponseRetries * 2000;
          console.warn(
            `%c[TOKEN REFRESH] Network/server error — retrying in ${delay/1000}s (attempt ${emptyResponseRetries}/3): ${error.message}`,
            'background: #a50; color: #fff; font-size: 12px; padding: 2px 6px;'
          );
          setTimeout(() => attemptRefresh(), delay);
          return;
        }

        // All retries exhausted — check if current token is still valid before logout
        isRefreshing = false;
        emptyResponseRetries = 0;

        let tokenStillValid = false;
        const currentToken = AuthService.token;
        if (currentToken) {
          try {
            const decoded = jwtDecode<JWTPayload>(currentToken);
            tokenStillValid = decoded.exp * 1000 - Date.now() > 0;
          } catch { /* can't decode */ }
        }

        if (tokenStillValid) {
          rejectPendingSubscribers(error);
          toast.warning('Unable to renew session — will retry shortly.', { autoClose: 3000 });
          setTimeout(() => getRefreshToken(), 30000);
        } else if (currentToken) {
          rejectPendingSubscribers(error);
          logger.warn('API', 'Token refresh failed after retries and token expired — logging out', {
            status: error.response?.status,
            message: error.message
          });
          AuthService.setLogoutReason(`Token refresh failed after retries (${error.response?.status || 'network error'}: ${error.message})`);
          toast.info('Session could not be renewed. Please log in again.', { autoClose: 3000 });
          AuthService.globalLogout();
        }
      });
    });
  }

  attemptRefresh();
}

/**
 * Reject all pending subscriber promises (called when token refresh fails)
 */
function rejectPendingSubscribers(error: any): void {
  failedSubscribers.forEach(reject => reject(error));
  subscribers = [];
  failedSubscribers = [];
}

function subscribeTokenRefresh(cb: TokenRefreshCallback): void {
  subscribers.push(cb);
}

function onRefreshed(token: string): void {
  subscribers.map(cb => cb(token));
}

/**
 * Shared token refresh entry point for non-Axios callers (e.g., fetch-based requests).
 * Subscribes to the unified refresh mechanism so only ONE refresh request is ever in-flight.
 * Safe to call concurrently — getRefreshToken() is guarded by isRefreshing flag.
 */
export function requestTokenRefresh(onSuccess: (token: string) => void, onFailure: (error: any) => void): void {
  subscribeTokenRefresh(onSuccess);
  failedSubscribers.push(onFailure);
  getRefreshToken();
}

function extractResourceType(url?: string): string {
  if (!url) return 'Resource';

  // Extract resource type from URL path
  // Examples: /datasource/123 -> Datasource, /flows/456 -> Flow, /flowdatasources/789 -> Flow Datasource
  const match = url.match(/\/(datasource|flowdatasource|flow|component|api|connection|script|group|project)s?\/[^/]+/i);
  if (match && match[1]) {
    // Handle compound names like "flowdatasource"
    if (match[1].toLowerCase() === 'flowdatasource') {
      return 'Flow Datasource';
    }
    const resourceType = match[1].charAt(0).toUpperCase() + match[1].slice(1);
    return resourceType;
  }

  return 'Resource';
}

function triggerExtension(response: AxiosResponse<ApiResponseData>): void {
  if (response.data.success && isSendErrorMsg) {
    isSendErrorMsg = false;

    extensionDispatch({
      type: "network-connection",
      connection: true
    });
  }

  if (response.data.error && !isSendErrorMsg) {
    switch (response.data.error.code) {
      case 401:
        break;
      case 500:
        if (response.data.error.description) {
          // Check if this is a datasource error for flowdatasources endpoint
          const isDatasourceError = response.data.error.description?.toLowerCase().includes('datasource') ||
                                     response.data.error.description?.toLowerCase().includes('cannot find');
          const isFlowDatasourceEndpoint = response.config?.url?.includes('/flowdatasources/');

          // Suppress datasource errors for flowdatasources endpoint (expected for empty flows)
          if (isDatasourceError && isFlowDatasourceEndpoint) {
            // Expected error for empty flows - suppress
          } else {
            // Show all other errors
            toast.error(response.data.error.description);
          }
        }
        break;
      default:
        isSendErrorMsg = true;
        extensionDispatch({
          type: "network-connection",
          connection: false
        });
        break;
    }
  }
}
